import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, ThumbsUp, Target, Trophy, Save, Plus, Trash2, MessageSquare, SlidersHorizontal } from 'lucide-react';
import PerformancePeriodPicker, { fmtDMY, mlabel } from './PerformancePeriodPicker';
import { useAuth } from '../../contexts/AuthContext';

const inr = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—');
const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);


const BAND_CLS = {
  A: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  B: 'bg-sky-50 text-sky-700 border-sky-200',
  C: 'bg-amber-50 text-amber-700 border-amber-200',
  D: 'bg-rose-50 text-rose-700 border-rose-200',
};
const BLOCK_META = {
  business: { l: 'Business', cls: 'text-emerald-700' },
  delivery: { l: 'Delivery', cls: 'text-violet-700' },
  demos: { l: 'Demos', cls: 'text-sky-700' },
};

const MEDIAN_KEY = {
  business_value: 'business_ex_gst', collection: 'collection_pct', dpr: 'dpr_pct',
  syllabus: 'syllabus_pct', attendance: 'attendance_pct', conversion: 'conversion_pct',
};

/** One trainer's FY sheet — the document a manager walks into the appraisal with. */
export default function TrainerPerformanceSheet({ trainerId, fy, onClose, onNote, onEditWeights, refreshKey = 0, canEdit = true }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState('');
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState('');
  const [period, setPeriod] = useState({ kind: 'fy' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/training/trainer-performance/trainer/${trainerId}`, {
        params: {
          fy: period.fy || fy,
          month: period.kind === 'month' ? period.month : undefined,
          quarter: period.kind === 'quarter' ? period.quarter : undefined,
          date_from: period.kind === 'custom' ? period.date_from : undefined,
          date_to: period.kind === 'custom' ? period.date_to : undefined,
        },
      });
      setD(r.data);
      setRating(r.data.review?.rating || '');
      setRemark(r.data.review?.remark || '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the sheet'); onClose?.(); }
    finally { setLoading(false); }
  }, [trainerId, fy, period, onClose]);

  useEffect(() => { setD(null); setPeriod({ kind: 'fy' }); }, [trainerId]);
  useEffect(() => { if (trainerId) load(); }, [trainerId, load, refreshKey]);

  const onSheetPeriod = useCallback((p) => {
    const next = p.fyChanged ? { kind: 'fy', fy: p.fy } : p;
    setPeriod((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, []);

  const saveReview = async () => {
    setSaving(true);
    try {
      await api.put(`/training/trainer-performance/trainer/${trainerId}/review`, { fy: d.fy, rating, remark });
      toast.success('Rating saved to this year’s review');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the rating'); }
    finally { setSaving(false); }
  };

  const removeNote = async (m) => {
    try {
      await api.delete(`/training/trainer-performance/trainer/${trainerId}/note/${m}`, { params: { fy: d.fy } });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const getPdf = async (variant) => {
    setPdfBusy(variant);
    try {
      const r = await api.get(`/training/trainer-performance/trainer/${trainerId}/pdf`, {
        params: {
          fy: period.fy || fy, variant,
          month: period.kind === 'month' ? period.month : undefined,
          quarter: period.kind === 'quarter' ? period.quarter : undefined,
          date_from: period.kind === 'custom' ? period.date_from : undefined,
          date_to: period.kind === 'custom' ? period.date_to : undefined,
        },
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      a.download = `performance-review-${d.trainer.name.replace(/ /g, '-')}${d.trainer.employee_id ? `_${d.trainer.employee_id}` : ''}_${d.period_slug}_${variant === 'trainer' ? 'Trainer_Copy' : 'Management'}.pdf`;
      a.click();
    } catch { toast.error('PDF export failed'); }
    finally { setPdfBusy(''); }
  };

  const s = d?.summary || {};
  const restricted = !!d?.restricted;
  const editable = canEdit && !restricted;

  return (
    <Dialog open={!!trainerId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto bg-white" data-testid="perf-sheet">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            {d?.trainer?.name || 'Trainer'}
            {s.band && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${BAND_CLS[s.band] || ''}`}>{`${s.band} · ${s.label}`}</span>}
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            {d?.trainer?.employee_id ? <>Employee ID <b>{d.trainer.employee_id}</b> · </> : null}
            {d?.period}
            {d?.rank ? <> · ranked <b>#{d.rank}</b> of {d.peers} trainers on the index</> : null}
          </DialogDescription>
          {d && (
            <div className="flex flex-wrap items-center gap-2 pt-1.5">
              <PerformancePeriodPicker compact fy={d.fy} fyOptions={d.fy_options || []} months={d.fy_month_options || []}
                currentQuarter={d.current_quarter || 1} value={period} onChange={onSheetPeriod} />
              {!restricted && (
                <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => getPdf('manager')} disabled={!!pdfBusy} data-testid="sheet-pdf-manager">
                  {pdfBusy === 'manager' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <span className="mr-1.5 px-1 py-[1px] rounded-[3px] bg-rose-600 text-white text-[8.5px] font-bold tracking-wide">PDF</span>}
                  Management copy
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => getPdf('trainer')} disabled={!!pdfBusy} data-testid="sheet-pdf-trainer">
                {pdfBusy === 'trainer' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <span className="mr-1.5 px-1 py-[1px] rounded-[3px] bg-slate-700 text-white text-[8.5px] font-bold tracking-wide">PDF</span>}
                Trainer copy
              </Button>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Building the sheet…</div>
        ) : !d ? null : (
          <div className="space-y-5">
            {/* Index + block scores */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {!restricted && (
                <Card className="p-4 border bg-slate-900 text-white" data-testid="sheet-index">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">FY Performance Index</p>
                  <p className="text-3xl font-bold tabular-nums">{s.index ?? '—'}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.label} · {s.coverage_pct}% of the weights measurable</p>
                </Card>
              )}
              {Object.entries(BLOCK_META).map(([k, meta]) => {
                const b = (s.blocks || {})[k];
                if (!b) return null;
                return (
                  <Card key={k} className="p-4 border" data-testid={`sheet-block-${k}`}>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">{meta.l} · weight {b.weight}</p>
                    <p className={`text-2xl font-bold tabular-nums ${meta.cls}`}>{b.score ?? '—'}</p>
                  </Card>
                );
              })}
            </div>

            {/* Headline numbers */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sheet-headline">
              {[
                !restricted && ['Business excl. GST', inr(s.business_ex_gst)],
                !restricted && ['Collected', `${inr(s.collected)} · ${pct(s.collection_pct)}`],
                ['Batches', `${s.batches_completed ?? 0} of ${s.batches ?? 0} completed`],
                ['Classes held', `${s.classes_held ?? 0} of ${s.classes_total ?? s.classes_held ?? 0}${s.classes_missed ? ` · ${s.classes_missed} cancelled` : ''}`],
                ['Students taught', s.students ?? 0],
                ['Demos', `${s.demo_admissions ?? 0} of ${s.demos ?? 0} converted`],
              ].filter(Boolean).map(([l, v]) => (
                <Card key={l} className="p-3 border">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{l}</p>
                  <p className="text-[14px] font-semibold text-slate-900 mt-0.5">{v}</p>
                </Card>
              ))}
            </div>

            {/* Component scoring */}
            <Card className="border overflow-hidden" data-testid="sheet-components">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center gap-2">
                <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">How the score was built</h3>
                {user?.role === 'admin' && onEditWeights && (
                  <button onClick={onEditWeights} className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-600 hover:underline" data-testid="sheet-edit-weights">
                    <SlidersHorizontal className="w-3 h-3" />Change the weights
                  </button>
                )}
              </div>
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-2.5">Parameter</th><th className="px-4 py-2.5">Block</th>
                  <th className="px-4 py-2.5 text-right">Weight</th><th className="px-4 py-2.5 text-right">Score</th>
                  <th className="px-4 py-2.5 text-right">Earned</th><th className="px-4 py-2.5 text-right">Org median</th>
                </tr></thead>
                <tbody>
                  {(s.components || []).map((c) => (
                    <tr key={c.key} className={`border-b border-slate-100 last:border-0 ${c.value === null ? 'opacity-50' : ''}`} data-testid={`sheet-comp-${c.key}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{c.label}</td>
                      <td className={`px-4 py-2.5 ${BLOCK_META[c.block]?.cls}`}>{BLOCK_META[c.block]?.l}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{c.weight}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">{c.value === null ? 'no data' : c.value}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{c.earned ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-400">
                        {(() => {
                          const mk = MEDIAN_KEY[c.key];
                          const v = mk ? (d.medians || {})[mk] : null;
                          if (v === null || v === undefined) return '—';
                          return mk === 'business_ex_gst' ? inr(v) : pct(v);
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Monthly trend — the 1:1 discussion sheet */}
            <Card className="border overflow-hidden" data-testid="sheet-months">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70">
                <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">Month by month</h3>
                <p className="text-[11px] text-slate-500">Use this in the monthly 1:1 — the trend matters more than any single month.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-3 py-2.5">Month</th><th className="px-3 py-2.5 text-right">Batches</th>
                    <th className="px-3 py-2.5 text-right">Completed</th><th className="px-3 py-2.5 text-right">Classes</th>
                    {!restricted && <th className="px-3 py-2.5 text-right">Business excl. GST</th>}
                    {!restricted && <th className="px-3 py-2.5 text-right">Collected</th>}
                    <th className="px-3 py-2.5 text-right">Class %</th><th className="px-3 py-2.5 text-right">Syllabus %</th>
                    <th className="px-3 py-2.5 text-right">Attend %</th><th className="px-3 py-2.5 text-right">Demos</th>
                    <th className="px-3 py-2.5 text-right">Conv %</th><th className="px-3 py-2.5 text-right">Lost</th>
                  </tr></thead>
                  <tbody>
                    {(d.months || []).map((m) => (
                      <tr key={m.month} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`sheet-month-${m.month}`}>
                        <td className="px-3 py-2.5 font-semibold text-slate-800">{mlabel(m.month)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{m.batches || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{m.batches_completed || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{m.classes_held || '—'}</td>
                        {!restricted && <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{m.business_ex_gst ? inr(m.business_ex_gst) : '—'}</td>}
                        {!restricted && <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{m.collected ? inr(m.collected) : '—'}</td>}
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(m.dpr_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(m.syllabus_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(m.attendance_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{m.demos || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(m.conversion_pct)}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${m.demo_lost ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{m.demo_lost || '—'}</td>
                      </tr>
                    ))}
                    {(d.months || []).length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-[12px] text-slate-400">No months in this FY yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Talking points */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4 border border-emerald-200 bg-emerald-50/50 space-y-2" data-testid="sheet-strengths">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5"><ThumbsUp className="w-3.5 h-3.5" />Working well</p>
                {(d.strengths || []).map((x) => (
                  <p key={x.label} className="text-[12.5px] text-slate-700"><b>{x.label}</b> — {x.detail}</p>
                ))}
                {(d.strengths || []).length === 0 && <p className="text-[12px] text-slate-500">Nothing is above target yet this year.</p>}
              </Card>
              <Card className="p-4 border border-amber-200 bg-amber-50/60 space-y-2" data-testid="sheet-improvements">
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />Areas to improve</p>
                {(d.improvements || []).map((x) => (
                  <p key={x.label} className="text-[12.5px] text-slate-700">
                    <b>{x.label}</b> — {x.detail}
                    {x.median !== undefined && x.median !== null ? <span className="text-slate-500"> (team median {x.median})</span> : null}
                  </p>
                ))}
                {(d.improvements || []).length === 0 && <p className="text-[12px] text-slate-500">Everything is at or above target. Worth saying so out loud.</p>}
              </Card>
            </div>

            {/* Monthly 1:1 notes */}
            <Card className="border overflow-hidden" data-testid="sheet-notes">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">Monthly review notes</h3>
                <span className="text-[11px] text-slate-500">— what was discussed and what to fix by the next one</span>
                {editable && (
                  <Button size="sm" variant="outline" className="h-7 ml-auto text-[11.5px]"
                    onClick={() => onNote?.({ trainer_id: trainerId, display_name: d.trainer?.name, employee_id: d.trainer?.employee_id })}
                    data-testid="sheet-note-add">
                    <Plus className="w-3 h-3 mr-1" />Add / edit a note
                  </Button>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {(d.notes || []).map((n) => (
                  <div key={n.month} className="px-4 py-3 flex items-start gap-3" data-testid={`sheet-note-${n.month}`}>
                    <span className="text-[11px] font-bold text-slate-500 w-16 shrink-0 pt-0.5">{mlabel(n.month)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12.5px] text-slate-800">{n.note}</p>
                      {n.next_actions && <p className="text-[12px] text-amber-800 mt-0.5"><b>Next:</b> {n.next_actions}</p>}
                      <p className="text-[10.5px] text-slate-400 mt-0.5">{n.by} · {String(n.at).slice(0, 10)}</p>
                    </div>
                    {editable && (
                      <button onClick={() => removeNote(n.month)} className="text-slate-300 hover:text-rose-600" title="Delete this note" data-testid={`sheet-note-del-${n.month}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {(d.notes || []).length === 0 && <p className="px-4 py-4 text-[12px] text-slate-400">No notes yet this year — use “Add / edit a note” above.</p>}
              </div>
            </Card>

            {/* Appraisal outcome */}
            {editable && (
              <Card className="p-4 border border-violet-200 bg-violet-50/40 space-y-3" data-testid="sheet-review">
                <p className="text-[11px] font-bold uppercase tracking-wider text-violet-800">Appraisal outcome · FY {d.fy}</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <p className="text-[11px] text-slate-500">Final rating</p>
                    <Select value={rating} onValueChange={setRating}>
                      <SelectTrigger className="h-9 w-44 bg-white text-[13px]" data-testid="review-rating"><SelectValue placeholder="Pick a rating" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {(d.ratings || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[260px] space-y-1">
                    <p className="text-[11px] text-slate-500">Manager remark (prints on the HR copy)</p>
                    <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
                      placeholder="Two lines the appraisal committee needs to read" className="bg-white text-[13px]" data-testid="review-remark" />
                  </div>
                  <Button size="sm" className="h-9 bg-violet-600 hover:bg-violet-700 text-white" onClick={saveReview}
                    disabled={saving || (!rating && remark.trim().length < 3)} data-testid="review-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save
                  </Button>
                </div>
                {d.review?.by && <p className="text-[11px] text-slate-500">Last saved by {d.review.by} on {String(d.review.at).slice(0, 10)}</p>}
              </Card>
            )}

            {/* Batches */}
            <Card className="border overflow-hidden" data-testid="sheet-batches">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-slate-400" />
                <h3 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">Batches in this FY · {(s.batch_rows || []).length}</h3>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 sticky top-0">
                    <th className="px-4 py-2.5">Batch</th><th className="px-4 py-2.5">Course</th><th className="px-4 py-2.5">Centre</th>
                    <th className="px-4 py-2.5">Window</th><th className="px-4 py-2.5 text-right">Students</th><th className="px-4 py-2.5">Status</th>
                  </tr></thead>
                  <tbody>
                    {(s.batch_rows || []).map((b) => (
                      <tr key={b.id} className="border-b border-slate-100 last:border-0">
                        <td className="px-4 py-2.5 font-mono text-[11px] text-slate-700">#{b.batch_number}</td>
                        <td className="px-4 py-2.5 text-slate-700">
                          {b.course_name}
                          {b.shared_with?.length ? (
                            <span className="block text-[10px] text-amber-700" title={`Shared with ${b.shared_with.join(', ')}`}>
                              shared with {b.shared_with.join(', ')} · your share {b.share_pct}%
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500">{b.centre_name || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-500">{fmtDMY(b.start_date)} → {b.end_date ? fmtDMY(b.end_date) : '—'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{b.students}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            b.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : b.status === 'Running' ? 'bg-violet-50 text-violet-700 border-violet-200'
                                : b.status === 'On Hold' ? 'bg-rose-50 text-rose-700 border-rose-200'
                                  : 'bg-slate-100 text-slate-500 border-slate-200'}`}>{b.status || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
